import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, X, UtensilsCrossed, IndianRupee, Paperclip, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * FnBPOActionModal — Phase 7, `fnb_po_review` (Sandeep Singh).
 * Approve  → Approx Price OPTIONAL (alphanumeric) + Food Menu + Groceries List (both optional).
 *            Remarks OPTIONAL.
 * Hold/Reject → Remarks MANDATORY (min 2 chars); price/attachments ignored.
 */
const PRICE_RE = /^[A-Za-z0-9 ,.\-₹$/()+]+$/;

export default function FnBPOActionModal({ open, onClose, onDone, requisition, action, currentUser, actingAsBanner = null }) {
  const [remarks, setRemarks] = useState("");
  const [price, setPrice] = useState("");
  const [menuFile, setMenuFile] = useState(null);
  const [groceriesFile, setGroceriesFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setPrice(""); setMenuFile(null); setGroceriesFile(null); setError("");
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, action, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const cfg = {
    approve: { label: "Approve", pastTense: "Approved", Icon: CheckCircle2, tone: "saffron",
               hint: "Optional: price, food menu, groceries list. Add anything Bursar / HOS will need." },
    on_hold: { label: "Place on Hold", pastTense: "On-hold", Icon: PauseCircle, tone: "gold" },
    reject:  { label: "Reject", pastTense: "Rejected", Icon: XCircle, tone: "danger" },
  }[action];
  if (!cfg) return null;

  const trimmedRemarks = remarks.trim();
  const trimmedPrice = price.trim();
  const remarksMandatory = action !== "approve";
  const remarksOk = true; // Phase 12.1 — no length rule
  const priceOk = !trimmedPrice || PRICE_RE.test(trimmedPrice);
  const canSubmit = remarksOk && priceOk && !busy;

  const btnCls = {
    saffron: "bg-[#c8632a] hover:bg-[#a04f22] border-[#a04f22]",
    gold:    "bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f]",
    danger:  "bg-[#b3261e] hover:bg-[#8a1c17] border-[#8a1c17]",
  }[cfg.tone];

  const uploadOne = async (file, kind) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return {
      field_name: kind, filename: data.filename, url: data.url, size: data.size, kind,
    };
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const payload = { action, remarks: trimmedRemarks };
      if (action === "approve") {
        if (trimmedPrice) payload.fnb_approx_price = trimmedPrice;
        const atts = [];
        try {
          const m = await uploadOne(menuFile, "food_menu");
          if (m) atts.push(m);
          const g = await uploadOne(groceriesFile, "groceries_list");
          if (g) atts.push(g);
        } catch {
          toast.warning("Attachment upload failed — approving without it.");
        }
        if (atts.length) payload.attachments = atts;
      }
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, payload);
      toast.success(`${cfg.pastTense} — ${requisition.ref_code}`);
      onDone?.(data); onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         onClick={onClose} data-testid="fnb-po-modal">
      <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="text-[#c8632a]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              F&B PO · {cfg.label} — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong> on behalf of the assigned approver.</div>
            </div>
          )}
          {cfg.hint && (
            <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#c8632a] px-3 py-2 rounded">
              <strong>{cfg.hint}</strong>
            </div>
          )}
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded">{error}</div>
          )}

          {action === "approve" && (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Approx Price
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">optional · alphanumeric</span>
                </span>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
                  <input type="text" value={price} onChange={(e) => setPrice(e.target.value.slice(0, 120))}
                         placeholder="e.g. ₹ 12,500"
                         className={`input-vgws pl-9 ${trimmedPrice && !priceOk ? "!border-[#b3261e]" : ""}`}
                         data-testid="fnb-approx-price" />
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Food Menu <span className="text-[#6e7770] text-xs font-normal">(optional)</span>
                </span>
                <div className="flex items-center gap-2">
                  <input type="file" onChange={(e) => setMenuFile(e.target.files?.[0] || null)}
                         accept="image/*,application/pdf,.doc,.docx"
                         className="text-xs text-[#4a524c]"
                         data-testid="fnb-po-food-menu" />
                  {menuFile && (
                    <span className="text-xs text-[#c8632a] inline-flex items-center gap-1">
                      <Paperclip size={12} /> {menuFile.name}
                    </span>
                  )}
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Groceries List <span className="text-[#6e7770] text-xs font-normal">(optional)</span>
                </span>
                <div className="flex items-center gap-2">
                  <input type="file" onChange={(e) => setGroceriesFile(e.target.files?.[0] || null)}
                         accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                         className="text-xs text-[#4a524c]"
                         data-testid="fnb-po-groceries" />
                  {groceriesFile && (
                    <span className="text-xs text-[#c8632a] inline-flex items-center gap-1">
                      <Paperclip size={12} /> {groceriesFile.name}
                    </span>
                  )}
                </div>
              </label>
            </>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks {remarksMandatory
                ? <span className="text-[#b3261e]">*</span>
                : <span className="text-[#6e7770] text-xs font-normal">(optional)</span>}
            </span>
            <textarea ref={textareaRef} value={remarks} onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
                      rows={4}
                      placeholder={remarksMandatory ? "Describe your reason clearly…" : "Add any note for the record (optional)…"}
                      className={`input-vgws min-h-[100px] ${remarksMandatory && trimmedRemarks && !remarksOk ? "!border-[#b3261e]" : ""}`}
                      data-testid="fnb-po-remarks" />
          </label>
        </div>
        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={!canSubmit}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm ${btnCls} ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="fnb-po-confirm">
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <cfg.Icon size={16} />}
            {busy ? "Please wait…" : `Confirm ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
